import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { Button, Grid, Paper, Typography } from "@material-ui/core";
import EmailStatusOverview from "./EmailStatusOverview";
import DataItemsPopover from "./DetailItemsPopover";
import formatBytes from "utils/formatBytes";
import DataItem from "./DataItem";
import { useStyles } from "../styles";

import { EmailFlowLog } from "@dashboard-v3/api";
import EmailActions from "./EmailActions";

interface Props {
  email: EmailFlowLog;
}

export default function MailDetails(props: Props) {
  const { email } = props;
  const { t } = useTranslation("emailFlowLogs");
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const getDate = (timestamp: number) => {
    const format = "MMM D, YYYY, h:mm:ss A";
    return dayjs(timestamp).format(format);
  };

  const onBackToSearch = () => {
    const params = new URLSearchParams(location.search);

    history.push(`/emailflow-logs?${params.toString()}`);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <Button
        variant="contained"
        disableElevation
        size="small"
        color="primary"
        style={{ marginBottom: 18, alignSelf: "flex-end" }}
        onClick={onBackToSearch}
      >
        {t("emailDetails.goBackToSearch")}
      </Button>

      <Paper className={classes.mainDetailsContainer}>
        <EmailStatusOverview email={email} />
        <div>
          <Typography variant="subtitle2" style={{ marginBottom: 10 }}>
            {t("emailDetails.title")}
          </Typography>
          <Grid container spacing={2}>
            {Boolean(email.subject) && (
              <Grid item lg={4}>
                <DataItem
                  type="column"
                  label={t("emailDetails.subject.label")}
                  data={email.subject}
                />
              </Grid>
            )}
            {Boolean(email.from) && (
              <Grid item lg={4}>
                <DataItem
                  type="column"
                  label={t("emailDetails.from.label")}
                  data={email.from}
                />
              </Grid>
            )}
            <Grid item lg={4}>
              <DataItem
                type="column"
                label={t("emailDetails.messageId.label")}
                data={email.messageId}
              />
            </Grid>
            <Grid item lg={4}>
              <DataItem
                type="column"
                label={t("emailDetails.createdAt.label")}
                data={getDate(email.createdAt)}
              />
            </Grid>
            {Boolean(email.to) && (
              <Grid item lg={4}>
                <DataItem
                  type="column"
                  label={t("emailDetails.to.label")}
                  data={
                    <DataItemsPopover
                      total={email.to.split(",").length}
                      actionLabel={t("emailDetails.to.value", {
                        count: email.to.split(",").length,
                      })}
                      items={email.to.split(",")}
                    />
                  }
                />
              </Grid>
            )}
            <Grid item lg={4}>
              <DataItem
                type="column"
                label={t("emailDetails.recipients.label")}
                data={
                  <DataItemsPopover
                    total={email.totalRecipients}
                    actionLabel={t("emailDetails.recipients.value", {
                      count: email.totalRecipients,
                    })}
                    items={email.recipients}
                  />
                }
              />
            </Grid>
            <Grid item lg={4}>
              <DataItem
                type="column"
                label={t("emailDetails.sender.label")}
                data={email.sender}
              />
            </Grid>

            <Grid item lg={4}>
              <DataItem
                type="column"
                label={t("emailDetails.attachments.label")}
                data={
                  <DataItemsPopover
                    total={email.totalAttachments}
                    actionLabel={t("emailDetails.attachments.value", {
                      count: email.totalAttachments,
                    })}
                    items={email.attachments}
                  />
                }
              />
            </Grid>
            <Grid item lg={4}>
              <DataItem
                type="column"
                label={t("emailDetails.initialSize.label")}
                data={formatBytes(email.bytes)}
              />
            </Grid>
            <Grid item lg={4}>
              <DataItem
                type="column"
                label={t("emailDetails.tnef.label")}
                data={email.isTnef ? t("common:yes") : t("common:no")}
              />
            </Grid>
          </Grid>
        </div>
        <EmailActions email={email} />
      </Paper>
    </div>
  );
}
